import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSocialPostCaptions from 'hooks/useSocialPostCaptions';

import {
  isErrorSocialPostCaptionsSelector,
  socialSharePostTextSelector,
} from 'redux/modules/download';
import { showError } from 'redux/modules/notification';
import { Dispatch, State } from 'redux/types';
import { SocialSharePlatform } from 'types';
import { RegenerateTextValues } from './SocialShareModalRegenerateTextButton/types';
import useSocialShareConnectedModal from './useSocialShareConnectedModal';

export interface UseSocialShareDescriptionResult {
  description: string;
  setDescription: (newDescription: string) => void;
  resetDescription: () => void;
  regenerateSocialPostCaptions: (args: RegenerateTextValues) => void;
}

/**
 * Manages the social share text (caption/description) shown in the modal.
 * - Store holds the source of truth: either default post text or platform caption.
 * - Local state is only a user override: when set, we show it; when undefined,
 *   we show the value from the store. So when the caption loads, the displayed
 *   value updates automatically if the user hasn't edited.
 */
export default function useSocialShareDescription(): UseSocialShareDescriptionResult {
  const [userOverride, setUserOverride] = useState<string | undefined>(
    undefined,
  );

  const { params } = useSocialShareConnectedModal();

  const platform = params?.platform;

  const postText = useSelector((state: State) =>
    socialSharePostTextSelector(platform as SocialSharePlatform, state),
  );
  const isSocialPostCaptionsError = useSelector(
    isErrorSocialPostCaptionsSelector,
  );

  const dispatch = useDispatch<Dispatch>();

  const { createSocialPostCaptions } = useSocialPostCaptions();

  const setDescription = useCallback((value: string) => {
    setUserOverride(value);
  }, []);

  const resetDescription = useCallback((): void => {
    setUserOverride(undefined);
  }, []);

  const handleRegenerateSocialPostCaptions = useCallback(
    ({ formality, tone }: RegenerateTextValues): void => {
      createSocialPostCaptions({
        platform: params?.platform,
        formality,
        tone,
      });
    },
    [createSocialPostCaptions, params?.platform],
  );

  useEffect(() => {
    if (isSocialPostCaptionsError) {
      dispatch(
        showError({
          message: 'Error generating text',
        }),
      );
    }
  }, [isSocialPostCaptionsError, dispatch]);

  return {
    description: userOverride ?? postText,
    setDescription,
    resetDescription,
    regenerateSocialPostCaptions: handleRegenerateSocialPostCaptions,
  };
}
